// SettleBinary — operator-only route (/settle-binary) for resolving
// binary Maniswap markets (I-074 P3c, the settlement surface).
//
// Reads OPEN binary markets with the anon client (binary_markets is
// anon-readable per the RLS read policy). The privileged settle goes
// through POST /api/settle-binary, which holds the service-role key and
// enforces the OPERATOR_SECRET Bearer gate — see api/settle-binary.ts.
// Nothing here can settle on its own; without the correct secret the
// endpoint returns 401. The secret lives only in this component's state
// + sessionStorage (cleared when the tab closes); it is never persisted
// to localStorage and never logged.
//
// Standalone route (rendered outside .cockpit-main), so the mobile-
// jailbreak CSS sharp edges do not apply; self-contained inline styles
// match BinaryMarketsPanel / TruthEngine.

import React, { useCallback, useEffect, useState } from 'react';
import { supabase, isSupabaseEnabled } from '../lib/supabaseClient';
import { binaryProbYes } from '../lib/binaryPricing';

type Side = 'YES' | 'NO';

interface OpenMarket {
  market_id: string;
  question: string;
  yes_reserve: number;
  no_reserve: number;
  p: number;
  amm_collateral: number;
}

interface SettleResult {
  market_id: string;
  winning_side: Side;
  positions_paid: number;
  amm_winning_shares: number;
  maker_residual: number;
  rake_accrued: number;
}

const SELECT_COLS = 'market_id,question,yes_reserve,no_reserve,p,amm_collateral,status';
const SECRET_KEY = 'operator_secret';
const pct = (x: number) => `${(x * 100).toFixed(1)}%`;

export default function SettleBinary(): React.JSX.Element {
  const [secret, setSecret] = useState<string>(() => {
    try { return sessionStorage.getItem(SECRET_KEY) ?? ''; } catch { return ''; }
  });
  const [markets, setMarkets] = useState<OpenMarket[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Per-market UI state keyed by market_id.
  const [pending, setPending] = useState<{ id: string; side: Side } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, SettleResult>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!isSupabaseEnabled() || !supabase) {
      setLoadError('Supabase not configured (this route is Arena-only).');
      setMarkets([]);
      return;
    }
    const { data, error } = await supabase
      .from('binary_markets')
      .select(SELECT_COLS)
      .eq('status', 'OPEN')
      .order('created_at', { ascending: true });
    if (error) {
      setLoadError(error.message);
      setMarkets([]);
      return;
    }
    setLoadError(null);
    setMarkets(
      (data ?? []).map((m): OpenMarket => ({
        market_id: String(m.market_id),
        question: String(m.question),
        yes_reserve: Number(m.yes_reserve),
        no_reserve: Number(m.no_reserve),
        p: Number(m.p),
        amm_collateral: Number(m.amm_collateral),
      })),
    );
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSecretChange = (v: string) => {
    setSecret(v);
    try { sessionStorage.setItem(SECRET_KEY, v); } catch { /* storage blocked */ }
  };

  async function confirmSettle(m: OpenMarket, side: Side) {
    setBusyId(m.market_id);
    setErrors((e) => { const n = { ...e }; delete n[m.market_id]; return n; });
    try {
      const resp = await fetch('/api/settle-binary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${secret}`,
        },
        body: JSON.stringify({ market_id: m.market_id, winning_side: side }),
      });
      const json = await resp.json();
      if (!resp.ok || !json.ok) {
        setErrors((e) => ({
          ...e,
          [m.market_id]: resp.status === 401
            ? 'Unauthorized — wrong or missing operator secret.'
            : (json.error ?? `HTTP ${resp.status}`),
        }));
        return;
      }
      setResults((r) => ({ ...r, [m.market_id]: json.result as SettleResult }));
      setPending(null);
      await load(); // settled market drops out of the OPEN list
    } catch (err) {
      setErrors((e) => ({ ...e, [m.market_id]: err instanceof Error ? err.message : String(err) }));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{
      minHeight: '100vh', background: '#020617', color: '#e5e7eb',
      fontFamily: 'monospace', padding: '32px 20px',
    }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{
          color: '#F59E0B', fontSize: 11, letterSpacing: '0.22em',
          fontWeight: 700, marginBottom: 4,
        }}>
          OPERATOR · BINARY MARKET SETTLEMENT
        </div>
        <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 24, lineHeight: 1.6 }}>
          Resolving a market is irreversible: it pays every winning position, returns the
          AMM residual to the maker, and flips the market to SETTLED. Authorised server-side
          by the operator secret.
        </div>

        {/* Operator secret */}
        <label style={{ display: 'block', fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
          Operator secret
        </label>
        <input
          type="password" value={secret} placeholder="OPERATOR_SECRET"
          onChange={(e) => onSecretChange(e.target.value)}
          style={{
            width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.2)', background: 'transparent',
            color: 'inherit', fontFamily: 'monospace', marginBottom: 28,
          }} />

        {loadError && (
          <div style={{ color: '#f88', fontSize: 12, marginBottom: 16 }}>{loadError}</div>
        )}
        {markets && markets.length === 0 && !loadError && (
          <div style={{ fontSize: 13, opacity: 0.6 }}>No OPEN binary markets to settle.</div>
        )}
        {!markets && <div style={{ fontSize: 13, opacity: 0.6 }}>Loading…</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {(markets ?? []).map((m) => {
            const probYes = binaryProbYes(m.yes_reserve, m.no_reserve, m.p);
            const isPending = pending?.id === m.market_id;
            const busy = busyId === m.market_id;
            const result = results[m.market_id];
            const err = errors[m.market_id];
            return (
              <div key={m.market_id} style={{
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: 16,
              }}>
                <div style={{ fontSize: 14, marginBottom: 6 }}>{m.question}</div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 14 }}>
                  P(YES) <strong>{pct(probYes)}</strong>
                  <span style={{ opacity: 0.5 }}> · AMM collateral {m.amm_collateral.toFixed(2)} OC · {m.market_id}</span>
                </div>

                {!isPending ? (
                  <div style={{ display: 'flex', gap: 8 }}>
                    {(['YES', 'NO'] as Side[]).map((s) => (
                      <button key={s} disabled={!secret || busy}
                        onClick={() => setPending({ id: m.market_id, side: s })}
                        style={{
                          flex: 1, padding: '9px 0', borderRadius: 8, fontWeight: 700,
                          border: '1px solid rgba(255,255,255,0.2)', color: 'inherit',
                          background: s === 'YES' ? '#1f7a4d' : '#7a1f2e',
                          cursor: (!secret || busy) ? 'default' : 'pointer',
                          opacity: (!secret || busy) ? 0.5 : 1,
                        }}>
                        Resolve {s} WINS
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    border: '1px solid #F59E0B', borderRadius: 8, padding: 12, fontSize: 13,
                  }}>
                    <div style={{ marginBottom: 10 }}>
                      Settle <strong>{pending.side}</strong> as the winner? This is irreversible.
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button disabled={busy} onClick={() => confirmSettle(m, pending.side)}
                        style={{
                          flex: 1, padding: '9px 0', borderRadius: 8, fontWeight: 700, border: 'none',
                          background: '#F59E0B', color: '#020617',
                          cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
                        }}>
                        {busy ? 'Settling…' : `Confirm — ${pending.side} WINS`}
                      </button>
                      <button disabled={busy} onClick={() => setPending(null)}
                        style={{
                          flex: 1, padding: '9px 0', borderRadius: 8,
                          border: '1px solid rgba(255,255,255,0.2)', background: 'transparent',
                          color: 'inherit', cursor: busy ? 'default' : 'pointer',
                        }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {err && <div style={{ color: '#f88', fontSize: 12, marginTop: 10 }}>{err}</div>}
                {result && (
                  <div style={{ color: '#8f8', fontSize: 12, marginTop: 10, lineHeight: 1.6 }}>
                    ✓ Settled {result.winning_side} WINS — {result.positions_paid} position(s) paid,
                    {' '}maker residual {Number(result.maker_residual).toFixed(2)} OC,
                    {' '}rake accrued {Number(result.rake_accrued).toFixed(2)} OC.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
